import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useProject, useCreateProject, useUpdateProject, useDeleteProjectImage } from '../../hooks/useProjects';
import { ArrowLeft, Upload, Check, X } from 'lucide-react';

interface FormValues {
  title: string;
  description: string;
  location: string;
  year: number | undefined;
  category: string;
  isVisible: boolean;
}

const CATEGORIES = ['Residential', 'Commercial', 'Hospitality', 'Interior', 'Exterior', 'Landscape'];

export function AdminProjectForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const { data: project, isLoading } = useProject(id ? parseInt(id) : 0);
  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject(id ? parseInt(id) : 0);
  const deleteImageMutation = useDeleteProjectImage();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      location: '',
      year: undefined,
      category: '',
      isVisible: true,
    },
  });

  useEffect(() => {
    if (project && isEditMode) {
      reset({
        title: project.title,
        description: project.description || '',
        location: project.location || '',
        year: project.year || undefined,
        category: project.category || '',
        isVisible: project.isVisible,
      });
    }
  }, [project, isEditMode, reset]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setImageFiles((prev) => [...prev, ...files]);

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeNewImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = async (imageId: number) => {
    if (!id) return;
    if (confirm('Bu görseli silmek istediğinize emin misiniz?')) {
      try {
        await deleteImageMutation.mutateAsync({ projectId: parseInt(id), imageId });
      } catch (error) {
        console.error('Image delete failed:', error);
      }
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const formData = {
        ...data,
        imageFiles: imageFiles.length > 0 ? imageFiles : undefined,
      };

      if (isEditMode && id) {
        await updateMutation.mutateAsync(formData);
      } else {
        await createMutation.mutateAsync(formData);
      }
      navigate('/yonetim/projects');
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to save project. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-neutral-200 border-t-neutral-900 rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-white pt-32 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        {/* Back Button */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <Link
            to="/yonetim/projects"
            className="inline-flex items-center gap-2 mb-8 text-neutral-600 hover:text-neutral-900 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="tracking-wide text-sm uppercase">Listeye Dön</span>
          </Link>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white p-10 shadow-lg shadow-black/5"
        >
          <h1 className="text-4xl tracking-[0.1em] uppercase font-light mb-10">
            {isEditMode ? t('admin.editProject') : t('admin.addProject')}
          </h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Existing Images (Edit Mode) */}
            {isEditMode && project?.images && project.images.length > 0 && (
              <div>
                <label className="block text-sm font-medium uppercase tracking-wide mb-4">
                  {t('projects.existingImages')}
                </label>
                <div className="flex flex-wrap gap-4">
                  {project.images.map((img) => (
                    <div key={img.id} className="relative w-32 h-32 group">
                      <img src={img.imageUrl} alt="" className="w-full h-full object-cover border border-neutral-200" />
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingImage(img.id)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium uppercase tracking-wide mb-4">
                {isEditMode ? t('projects.addMoreImages') : t('admin.image')}
              </label>

              {/* New image previews */}
              {imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-4 mb-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative w-32 h-32 group">
                      <img src={preview} alt="Preview" className="w-full h-full object-cover border border-neutral-200" />
                      <button
                        type="button"
                        onClick={() => removeNewImage(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <label className="cursor-pointer border-2 border-dashed border-neutral-300 hover:border-neutral-400 p-12 text-center transition-colors duration-300 group block">
                <div className="flex flex-col items-center gap-4">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="w-16 h-16 bg-neutral-100 group-hover:bg-neutral-200 flex items-center justify-center transition-colors"
                  >
                    <Upload className="w-8 h-8 text-neutral-400" />
                  </motion.div>
                  <div>
                    <p className="text-sm text-neutral-600 mb-1">{t('admin.uploadImage')}</p>
                    <p className="text-xs text-neutral-400">PNG, JPG - {t('projects.multipleAllowed')}</p>
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium uppercase tracking-wide mb-3">
                {t('marble.title')} <span className="text-red-600">*</span>
              </label>
              <input
                {...register('title', { required: 'Title is required' })}
                className="w-full px-4 py-4 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-shadow"
                placeholder={t('projects.titlePlaceholder')}
              />
              {errors.title && <p className="text-red-600 text-sm mt-2">{errors.title.message}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium uppercase tracking-wide mb-3">
                {t('marble.description')}
              </label>
              <textarea
                {...register('description')}
                rows={5}
                className="w-full px-4 py-4 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 resize-none transition-shadow"
                placeholder={t('projects.descriptionPlaceholder')}
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium uppercase tracking-wide mb-3">
                  {t('projects.category')}
                </label>
                <select
                  {...register('category')}
                  className="w-full px-4 py-4 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-shadow bg-white"
                >
                  <option value="">{t('projects.selectCategory')}</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{t(`projects.categories.${cat}`)}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium uppercase tracking-wide mb-3">
                  {t('projects.location')}
                </label>
                <input
                  {...register('location')}
                  className="w-full px-4 py-4 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-shadow"
                  placeholder="e.g., Istanbul, Turkey"
                />
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-medium uppercase tracking-wide mb-3">
                  {t('projects.year')}
                </label>
                <input
                  type="number"
                  {...register('year', { valueAsNumber: true })}
                  className="w-full px-4 py-4 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-shadow"
                  placeholder="e.g., 2024"
                />
              </div>

              {/* Is Visible */}
              <div className="flex items-center">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    {...register('isVisible')}
                    className="w-6 h-6 border-2 border-neutral-300 focus:ring-2 focus:ring-neutral-900 cursor-pointer"
                  />
                  <span className="text-sm font-medium uppercase tracking-wide group-hover:text-neutral-600 transition-colors">
                    {t('admin.visible')}
                  </span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t-2 border-neutral-200">
              <motion.button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-3 px-10 py-5 bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-400 disabled:cursor-not-allowed tracking-[0.2em] uppercase text-sm transition-colors duration-300 shadow-lg shadow-black/10"
              >
                <Check className="w-5 h-5" />
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : t('admin.save')}
              </motion.button>
              <motion.button
                type="button"
                onClick={() => navigate('/yonetim/projects')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-5 border-2 border-neutral-300 text-neutral-900 hover:bg-neutral-50 tracking-[0.2em] uppercase text-sm transition-colors duration-300"
              >
                {t('admin.cancel')}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
